// =============================================================================
// features/assistant/hooks/useAssistantProgress.ts — Passo atual do copiloto.
//
// O copiloto responde num único POST e o operador espera olhando a tela. Com o
// agente encadeando chamadas de LLM (~9-12s cada), a espera chega a dezenas de
// segundos — e um skeleton parado é indistinguível de um sistema travado.
//
// O grafo empurra cada round de ferramentas por Socket.io (sala `user:{userId}`,
// evento `assistant.progress`). Este hook escuta e devolve o passo corrente.
//
// LGPD (doc 17): o evento carrega SOMENTE nomes de ferramenta — nunca os
// argumentos, que levam lead_id/telefone/nome. O texto legível é montado aqui a
// partir do nome (progressLabels.ts).
// =============================================================================

import * as React from 'react';

import { useSocket } from '../../../lib/realtime/useSocket';
import { progressLabel } from '../progressLabels';

/** Payload do evento `assistant.progress` publicado pelo backend. */
interface AssistantProgressEvent {
  tools: string[];
  round: number;
}

function isProgressEvent(value: unknown): value is AssistantProgressEvent {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    Array.isArray(candidate['tools']) &&
    candidate['tools'].every((t) => typeof t === 'string') &&
    typeof candidate['round'] === 'number'
  );
}

export interface UseAssistantProgressResult {
  /** Texto do passo atual, ou `null` quando não há passo conhecido. */
  label: string | null;
  /** Zera o passo — chamado ao iniciar uma pergunta nova. */
  reset: () => void;
}

/**
 * Escuta o progresso do copiloto enquanto `isActive`.
 *
 * @param isActive `true` só enquanto há uma pergunta em voo. Fora disso o hook
 *   não assina nada: um listener vivo com o modal fechado acumularia passos de
 *   uma pergunta que a tela já esqueceu.
 */
export function useAssistantProgress(isActive: boolean): UseAssistantProgressResult {
  const socket = useSocket();
  const [state, setState] = React.useState<{ label: string; round: number } | null>(null);

  const reset = React.useCallback(() => {
    setState(null);
  }, []);

  React.useEffect(() => {
    if (!isActive || socket === null) return;

    const handler = (payload: unknown): void => {
      // Payload vem da rede: valida antes de usar. Evento malformado é ignorado
      // em silêncio — progresso nunca pode quebrar a tela da resposta.
      if (!isProgressEvent(payload)) return;

      setState((current) => {
        // Rounds podem chegar fora de ordem (fila + socket). Um round antigo
        // sobrescrevendo o atual faria o texto "andar para trás" na tela.
        if (current !== null && payload.round < current.round) return current;
        return { label: progressLabel(payload.tools), round: payload.round };
      });
    };

    socket.on('assistant.progress', handler);
    return () => {
      socket.off('assistant.progress', handler);
    };
  }, [isActive, socket]);

  // Pergunta terminou: o passo não faz mais sentido e some junto com o skeleton.
  React.useEffect(() => {
    if (!isActive) setState(null);
  }, [isActive]);

  return { label: state?.label ?? null, reset };
}
